import { call, put, select, takeLatest } from "typed-redux-saga";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { createCustomNotificationAction } from "../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { isWorkflowActivity } from "../../../utilities/activityUtils";
import { isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { handleOutdatedWorkflowActivity } from "../../../utilities/workflowUtils";
import { lockProcessError } from "../../processFormEngine/store/actions";

import JsonRpc2Error = JsonRpc2Response.JsonRpc2Error;

export function* processSaveFailedSaga() {
	yield* takeLatest(ActivityActions.save.failed.match, function* (action) {
		const activity = yield* select(ActivitySelectors.activityById(action.payload.params.activityId));
		const error = action.payload.error;
		if (activity && isWorkflowActivity(activity) && isJsonRpc2ResponseArray(error) && hasErrorCode(error, 403)) {
			yield* call(handleOutdatedWorkflowActivity, activity);
		} else {
			if (hasJsonRpc2ErrorCause(error) && hasSavingLockedError(error.cause)) {
				yield* put(lockProcessError({}));
				return;
			}
			yield* put(
				createCustomNotificationAction({
					activityId: action.payload.params.activityId,
					message: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.updatetask),
					severity: "error",
					group: "processSaveFailed"
				})
			);
			yield* put(ApplicationActions.setBusy(false));
			yield* put(ActivityActions.setDirty({ activityId: action.payload.params.activityId, dirty: true }));
		}
	});
}

function hasErrorCode(rpcResponses: JsonRpc2Response[], errorCode: number): boolean {
	return (
		rpcResponses.find(e => JsonRpc2Response.JsonRpc2Error.isInstance(e.error) && e.error.code === errorCode) !==
		undefined
	);
}

export function isJsonRpc2JsonRpc2ErrorArray(value: unknown): value is JsonRpc2Error[] {
	return Array.isArray(value) && value.every(x => JsonRpc2Error.isInstance(x));
}

export function hasJsonRpc2ErrorCause(error: unknown): error is Error & { cause: JsonRpc2Error[] } {
	return error instanceof Error && "cause" in error && isJsonRpc2JsonRpc2ErrorArray(error.cause);
}

/**
 * Extracts all {@code data.title.key} values from a {@link JsonRpc2Error} array
 * and returns {@code true} when any of them equals {@code "SAVING_LOCK_ERROR"}.
 */
export function hasSavingLockedError(errors: JsonRpc2Error[]): boolean {
	return errors
		.map(e => {
			const data = e.data;
			if (!data || typeof data !== "object") {
				return undefined;
			}
			const title = (data as Record<string, unknown>).title;
			if (!title || typeof title !== "object") {
				return undefined;
			}
			return (title as Record<string, unknown>).key;
		})
		.some(key => key === "SAVING_LOCKED_ERROR");
}
